import { apiClient } from "./api";
import { campaignService } from "./campaignService";
import { callService } from "./callService";
import { Campaign } from "../types/campaign";
import { CallStats } from "../types/call";

export interface DashboardStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalCalls: number;
  manualCalls: number;
  autoCalls: number;
  activeCalls: number;
  totalLeads: number;
  remainingLeads: number;
  calledLeads: number;
  interestedLeads: number;
  notInterestedLeads: number;
  followUpLeads: number;
  noAnswerLeads: number;
  doNotCallLeads: number;
  failedLeads: number;
  badNumberLeads: number;
  wrongNumberLeads: number;
  outOfBusinessLeads: number;
  unqualifiedLeads: number;
  dialedCalls: number;
  queuedCalls: number;
  completedCalls: number;
  successRate: number;
  sms: {
    totalSent: number;
    inboundReplies: number;
    openThreads: number;
    dncBlocks: number;
  };
}

export interface PerformanceData {
  date: string;
  calls: number;
  interested: number;
  successRate: number;
}

export interface CampaignStatusData {
  name: string;
  value: number;
  color: string;
}

export interface CallActivityData {
  date: string;
  completed: number;
  pending: number;
  failed: number;
}

export interface AgentMetrics {
  id: string;
  name: string;
  email: string;
  status: 'available' | 'ringing' | 'in-call' | 'offline';
  isAvailable: boolean;
  leadsContacted: number;
  totalCalls: number;
  totalCallHours: number;
  totalCallSeconds: number;
  interestedLeads: number;
  notInterestedLeads: number;
  statusCounts?: {
    interested: number;
    not_interested: number;
    follow_up: number;
    no_answer: number;
    busy: number;
    unreachable: number;
    failed: number;
    do_not_call: number;
    out_of_business: number;
    wrong_number: number;
    unqualified: number;
    gatekeeper: number;
    operator: number;
  };
}

export interface CampaignMetricsBreakdown {
  campaignId: string;
  campaignName: string;
  campaignStatus: string;
  lastCallAt?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  stats: {
    totalLeads: number;
    remainingLeads: number;
    calledLeads: number;
    interestedLeads: number;
    notInterestedLeads: number;
    followUpLeads: number;
    noAnswerLeads: number;
    doNotCallLeads: number;
    failedLeads: number;
    totalCalls: number;
  };
}

export interface DashboardSnapshot {
  stats: DashboardStats;
  campaignMetrics: CampaignMetricsBreakdown[];
}

const mapCampaignMetrics = (campaigns: any[]): CampaignMetricsBreakdown[] =>
  campaigns.map((c: any) => ({
    campaignId: c.campaignId,
    campaignName: c.campaignName,
    campaignStatus: c.campaignStatus,
    lastCallAt: c?.stats?.lastCallAt || null,
    updatedAt: c.updatedAt || null,
    createdAt: c.createdAt || null,
    stats: {
      totalLeads: Number(c?.stats?.totalLeads || 0),
      remainingLeads: Number(c?.stats?.remainingLeads || 0),
      calledLeads: Number(c?.stats?.calledLeads || 0),
      interestedLeads: Number(c?.stats?.interestedLeads || 0),
      notInterestedLeads: Number(c?.stats?.notInterestedLeads || 0),
      followUpLeads: Number(c?.stats?.followUpLeads || 0),
      noAnswerLeads: Number(c?.stats?.noAnswerLeads || 0),
      doNotCallLeads: Number(c?.stats?.doNotCallLeads || 0),
      failedLeads: Number(c?.stats?.failedLeads || 0),
      totalCalls: Number(c?.stats?.totalCalls || 0),
    },
  }));

const getDefaultDashboardStats = (): DashboardStats => ({
  totalCampaigns: 0,
  activeCampaigns: 0,
  totalCalls: 0,
  manualCalls: 0,
  autoCalls: 0,
  activeCalls: 0,
  totalLeads: 0,
  remainingLeads: 0,
  calledLeads: 0,
  interestedLeads: 0,
  notInterestedLeads: 0,
  followUpLeads: 0,
  noAnswerLeads: 0,
  doNotCallLeads: 0,
  failedLeads: 0,
  badNumberLeads: 0,
  wrongNumberLeads: 0,
  outOfBusinessLeads: 0,
  unqualifiedLeads: 0,
  dialedCalls: 0,
  queuedCalls: 0,
  completedCalls: 0,
  successRate: 0,
  sms: {
    totalSent: 0,
    inboundReplies: 0,
    openThreads: 0,
    dncBlocks: 0,
  },
});

const mapAggregatedStats = (aggregated: any): DashboardStats => ({
  totalCampaigns: Number(aggregated?.totalCampaigns || 0),
  activeCampaigns: Number(aggregated?.activeCampaigns || 0),
  totalCalls: Number(aggregated?.totalCalls || 0),
  manualCalls: Number(aggregated?.manualCalls || 0),
  autoCalls: Number(aggregated?.autoCalls || 0),
  activeCalls: Number(aggregated?.activeCalls || 0),
  totalLeads: Number(aggregated?.totalLeads || 0),
  remainingLeads: Number(aggregated?.remainingLeads || 0),
  calledLeads: Number(aggregated?.calledLeads || 0),
  interestedLeads: Number(aggregated?.interestedLeads || 0),
  notInterestedLeads: Number(aggregated?.notInterestedLeads || 0),
  followUpLeads: Number(aggregated?.followUpLeads || 0),
  noAnswerLeads: Number(aggregated?.noAnswerLeads || 0),
  doNotCallLeads: Number(aggregated?.doNotCallLeads || 0),
  failedLeads: Number(aggregated?.failedLeads || 0),
  badNumberLeads: Number(aggregated?.badNumberLeads || 0),
  wrongNumberLeads: Number(aggregated?.wrongNumberLeads || 0),
  outOfBusinessLeads: Number(aggregated?.outOfBusinessLeads || 0),
  unqualifiedLeads: Number(aggregated?.unqualifiedLeads || 0),
  dialedCalls: Number(aggregated?.dialedCalls ?? aggregated?.pendingCalls ?? 0),
  queuedCalls: Number(aggregated?.queuedCalls ?? 0),
  completedCalls: Number(aggregated?.successfulCalls || 0),
  successRate: Number(aggregated?.successRate || 0),
  sms: {
    totalSent: Number(aggregated?.sms?.totalSent || 0),
    inboundReplies: Number(aggregated?.sms?.inboundReplies || 0),
    openThreads: Number(aggregated?.sms?.openThreads || 0),
    dncBlocks: Number(aggregated?.sms?.dncBlocks || 0),
  },
});

export const dashboardService = {
  async getDashboardSnapshot(startDate?: string, endDate?: string): Promise<DashboardSnapshot> {
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get('/campaigns/stats', { params });
      const aggregated = response.data?.data?.aggregated || {};
      const campaigns = response.data?.data?.campaigns || [];

      return {
        stats: mapAggregatedStats(aggregated),
        campaignMetrics: mapCampaignMetrics(campaigns),
      };
    } catch (error) {
      console.error('Failed to fetch dashboard snapshot:', error);
      return {
        stats: getDefaultDashboardStats(),
        campaignMetrics: [],
      };
    }
  },

  // Get dashboard statistics using aggregated endpoint
  async getDashboardStats(startDate?: string, endDate?: string): Promise<DashboardStats> {
    const snapshot = await this.getDashboardSnapshot(startDate, endDate);
    return snapshot.stats;
  },

  // Get performance data for charts (last 7 days)
  async getPerformanceData(): Promise<PerformanceData[]> {
    try {
      // Use the aggregated stats endpoint
      const response = await apiClient.get("/campaigns/stats");
      const { aggregated, campaigns } = response.data.data;

      console.log("Campaigns found:", campaigns.length);

      // If no campaigns exist, return sample data for demonstration
      if (campaigns.length === 0) {
        console.log("No campaigns found, returning sample data");
        return this.generateSamplePerformanceData();
      }

      const totalCalls = aggregated.totalCalls;
      const totalInterested = aggregated.interestedLeads;

      console.log("Total calls across all campaigns:", totalCalls);
      console.log("Total interested across all campaigns:", totalInterested);

      // If no call data exists, return sample data
      if (totalCalls === 0) {
        console.log("No call data found, returning sample data");
        return this.generateSamplePerformanceData();
      }

      // Generate last 7 days data
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const today = new Date();
      const performanceData: PerformanceData[] = [];

      // For now, we'll distribute the total stats across the week
      // In a real implementation, this would come from daily analytics data
      const avgSuccessRate =
        totalCalls > 0 ? (totalInterested / totalCalls) * 100 : 0;

      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dayName = days[date.getDay()];

        // Distribute calls across the week with some variation
        const dayMultiplier = 0.1 + Math.random() * 0.2; // 10-30% of total per day
        const calls = Math.floor(totalCalls * dayMultiplier);
        const interested = Math.floor(calls * (avgSuccessRate / 100));
        const successRate = calls > 0 ? (interested / calls) * 100 : 0;

        performanceData.push({
          date: dayName,
          calls,
          interested,
          successRate: Math.round(successRate * 10) / 10,
        });
      }

      return performanceData;
    } catch (error) {
      console.error("Failed to fetch performance data:", error);
      // Return default data on error
      return [
        { date: "Sun", calls: 0, interested: 0, successRate: 0 },
        { date: "Mon", calls: 0, interested: 0, successRate: 0 },
        { date: "Tue", calls: 0, interested: 0, successRate: 0 },
        { date: "Wed", calls: 0, interested: 0, successRate: 0 },
        { date: "Thu", calls: 0, interested: 0, successRate: 0 },
        { date: "Fri", calls: 0, interested: 0, successRate: 0 },
        { date: "Sat", calls: 0, interested: 0, successRate: 0 },
      ];
    }
  },

  // Get campaign status distribution
  async getCampaignStatusData(): Promise<CampaignStatusData[]> {
    try {
      const campaignsResponse = await campaignService.getCampaigns();
      const campaigns = campaignsResponse?.campaigns || [];

      const statusCounts = campaigns.reduce(
        (acc, campaign) => {
          acc[campaign.status] = (acc[campaign.status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      const statusData: CampaignStatusData[] = [
        {
          name: "Active",
          value: statusCounts.active || 0,
          color: "hsl(var(--success))",
        },
        {
          name: "Paused",
          value: statusCounts.paused || 0,
          color: "hsl(var(--warning))",
        },
        {
          name: "Completed",
          value: statusCounts.completed || 0,
          color: "hsl(var(--primary))",
        },
        {
          name: "Draft",
          value: statusCounts.draft || 0,
          color: "hsl(var(--muted-foreground))",
        },
      ];

      // Filter out statuses with 0 values
      return statusData.filter((item) => item.value > 0);
    } catch (error) {
      console.error("Failed to fetch campaign status data:", error);
      return [];
    }
  },

  // Get call activity data (last 7 days)
  async getCallActivityData(): Promise<CallActivityData[]> {
    try {
      // Use the aggregated stats endpoint
      const response = await apiClient.get("/campaigns/stats");
      const { aggregated, campaigns } = response.data.data;

      // If no campaigns exist, return sample data for demonstration
      if (campaigns.length === 0) {
        console.log(
          "No campaigns found for call activity, returning sample data"
        );
        return this.generateSampleCallActivityData();
      }

      // Calculate totals from aggregated data
      const totalCompleted = aggregated.successfulCalls;
      const totalCalls = aggregated.totalCalls;
      const totalFailed = aggregated.failedCalls;

      // If no call data exists, return sample data
      if (totalCalls === 0) {
        console.log("No call activity data found, returning sample data");
        return this.generateSampleCallActivityData();
      }

      // Generate last 7 days data
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const today = new Date();
      const callActivityData: CallActivityData[] = [];

      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dayName = days[date.getDay()];

        // Distribute call activity across the week with some variation
        const dayMultiplier = 0.1 + Math.random() * 0.2; // 10-30% of total per day
        const completed = Math.floor(totalCompleted * dayMultiplier);
        const failed = Math.floor(totalFailed * dayMultiplier);
        const pending = Math.floor(totalCalls * 0.1 * dayMultiplier); // Assume 10% pending

        callActivityData.push({
          date: dayName,
          completed,
          pending,
          failed,
        });
      }

      return callActivityData;
    } catch (error) {
      console.error("Failed to fetch call activity data:", error);
      // Return default data on error
      return [
        { date: "Sun", completed: 0, pending: 0, failed: 0 },
        { date: "Mon", completed: 0, pending: 0, failed: 0 },
        { date: "Tue", completed: 0, pending: 0, failed: 0 },
        { date: "Wed", completed: 0, pending: 0, failed: 0 },
        { date: "Thu", completed: 0, pending: 0, failed: 0 },
        { date: "Fri", completed: 0, pending: 0, failed: 0 },
        { date: "Sat", completed: 0, pending: 0, failed: 0 },
      ];
    }
  },

  // Get recent campaigns for dashboard
  async getRecentCampaigns(limit: number = 5): Promise<Campaign[]> {
    try {
      const campaignsResponse = await campaignService.getCampaigns({
        page: 1,
        limit,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      return campaignsResponse?.campaigns || [];
    } catch (error) {
      console.error("Failed to fetch recent campaigns:", error);
      return [];
    }
  },

  // Get active campaigns for dashboard
  async getActiveCampaigns(limit: number = 5): Promise<Campaign[]> {
    try {
      const campaignsResponse = await campaignService.getCampaigns({
        status: ["active"],
        page: 1,
        limit,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      return campaignsResponse?.campaigns || [];
    } catch (error) {
      console.error("Failed to fetch active campaigns:", error);
      return [];
    }
  },

  // Get agent performance metrics for admin dashboard
  async getAgentMetrics(startDate?: string, endDate?: string): Promise<AgentMetrics[]> {
    try {
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await apiClient.get('/agents/metrics', { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to fetch agent metrics:', error);
      return [];
    }
  },

  async getCampaignMetricsBreakdown(
    startDate?: string,
    endDate?: string
  ): Promise<CampaignMetricsBreakdown[]> {
    const snapshot = await this.getDashboardSnapshot(startDate, endDate);
    return snapshot.campaignMetrics;
  },

  // Generate sample performance data for demonstration when no real data exists
  generateSamplePerformanceData(): PerformanceData[] {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const today = new Date();
    const performanceData: PerformanceData[] = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = days[date.getDay()];

      // Generate sample data with some variation
      const calls = Math.floor(Math.random() * 50) + 20; // 20-70 calls
      const interested = Math.floor(calls * (0.15 + Math.random() * 0.25)); // 15-40% interest rate
      const successRate = Math.round((interested / calls) * 100);

      performanceData.push({
        date: dayName,
        calls,
        interested,
        successRate,
      });
    }

    return performanceData;
  },

  // Generate sample call activity data for demonstration
  generateSampleCallActivityData(): CallActivityData[] {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const today = new Date();
    const callActivityData: CallActivityData[] = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = days[date.getDay()];

      // Generate sample data with some variation
      const completed = Math.floor(Math.random() * 40) + 15; // 15-55 completed
      const failed = Math.floor(Math.random() * 10) + 2; // 2-12 failed
      const pending = Math.floor(Math.random() * 15) + 5; // 5-20 pending

      callActivityData.push({
        date: dayName,
        completed,
        failed,
        pending,
      });
    }

    return callActivityData;
  },
};
